import { useState } from 'react';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useGlobalModals } from '@hooks/modals/useGlobalModals';
import { useLocales } from '@hooks/helpers/useLocales';
import useModalContext from '@hooks/context/useModalContext';
import { apiAdminService } from '@api';
import { removeEmptyArrays } from '@utils/index';
import {
    TGetAdminGiftCardsFilter,
    TAdminGiftCardsList,
    EGiftCardStatus
} from '@xeppt/xeppt-sdk/types';
import { toast } from 'react-toastify';

interface IProps {
    isInitialGiftCards?: boolean;
    customer_id?: string;
}

export const useGiftCards = ({ customer_id, isInitialGiftCards }: IProps) => {
    const { handleOpenApproveActionModal } = useGlobalModals();
    const { onClose, handleChangeLoading } = useModalContext();
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const [giftCards, setGiftCards] = useState<TAdminGiftCardsList[]>([]);
    const [filter, setFilter] = useState<TGetAdminGiftCardsFilter>({
        page: 1,
        page_size: 20,
        status: [],
        search: '',
        notification_send: undefined,
        notification_send_date_from: undefined,
        notification_send_date_to: undefined,
        activation_date_from: undefined,
        activation_date_to: undefined,
        amount_from: undefined,
        amount_to: undefined,
        available_amount_from: undefined,
        available_amount_to: undefined,
        order_field: 'created_at',
        order: 'DESC'
    });

    const { data: giftCardsData, handleRequest: refetchGiftCards } = useApiQuery({
        method: (filter?: TGetAdminGiftCardsFilter) => {
            return apiAdminService.getGiftCardsList({
                ...removeEmptyArrays(filter || {}),
                customer_id
            });
        },
        onSuccess: (data) => {
            if (data.page !== 1) {
                setGiftCards((state) => [...state, ...data.data]);
                setFilter((state) => ({ ...state, page: data.page }));
            } else {
                setGiftCards(data.data);
            }
        },
        isInitialRequest: isInitialGiftCards,
        deps: [customer_id]
    });

    const onChangeFilter = (data: Partial<TGetAdminGiftCardsFilter>) => {
        if (data.page !== undefined) {
            setFilter((state) => ({ ...state, ...data }));
            refetchGiftCards({ ...filter, ...data });
        } else {
            setFilter((state) => ({ ...state, ...data, page: 1 }));
            refetchGiftCards({ ...filter, ...data, page: 1 });
        }
    };

    const {
        data: giftCard,
        handleRequest: getSingleGiftCard,
        handleRemoveResponse: handleRemoveGiftCardData
    } = useApiQuery({
        method: (id?: string) => apiAdminService.getGiftCardById(id || ''),
        isInitialRequest: false
    });

    const { handleRequest: handleUpdateGiftCardStatus } = useApiMutation({
        method: ({
            id,
            status
        }: {
            id: string;
            status: Omit<
                EGiftCardStatus,
                EGiftCardStatus.ISSUED | EGiftCardStatus.CANCELLED | EGiftCardStatus.REDEEMED
            >;
        }) => {
            handleChangeLoading(true);
            return apiAdminService.changeGiftCardStatus(id, status);
        },
        onSuccess: (_, requestData) => {
            onClose();
            setGiftCards((prev) =>
                prev.map((card) =>
                    card.id === requestData.id
                        ? { ...card, status: requestData.status as EGiftCardStatus }
                        : card
                )
            );
            // Reset page to 1 so that pagination continues correctly after the list is refreshed
            setFilter((state) => ({ ...state, page: 1 }));
            refetchGiftCards({ ...filter, page: 1 });
            toast.success(requestSuccessLocale('gift_card_status_changed'));
        },
        onError: requestErrorLocale,
        onFinish: () => handleChangeLoading(false)
    });

    const handleOpenChangeStatusModal = ({
        id,
        status
    }: {
        id: string;
        status: Omit<
            EGiftCardStatus,
            EGiftCardStatus.ISSUED | EGiftCardStatus.CANCELLED | EGiftCardStatus.REDEEMED
        >;
    }) => {
        handleOpenApproveActionModal({
            title: 'Change gift card status',
            description: `Are you sure you want to change gift card status?`,
            onSubmit: () => handleUpdateGiftCardStatus({ id, status }),
            cancelLabel: 'cancel',
            confirmLabel: 'confirm'
        });
    };

    return {
        giftCards,
        giftCardsData,
        filter,
        refetchGiftCards,
        onChangeFilter,
        giftCard,
        getSingleGiftCard,
        handleRemoveGiftCardData,
        handleUpdateGiftCardStatus: handleOpenChangeStatusModal
    };
};
